/**
 * Mobile/APK-Friendly Table Alternatives
 * 
 * Provides better formatting options for mobile and APK users
 * 40% of users are on mobile/APK - tables break easily on small screens
 */

export const MOBILE_FRIENDLY_FORMATS = `
## 📱 MOBILE/APK-FRIENDLY ALTERNATIVES TO TABLES

**CRITICAL:** 40% of users are on mobile or APK. Tables with 3+ columns break on small screens. Use these alternatives instead.

---

### ✅ FORMAT 1: COMPARISON CARDS (Best for 2-4 items)

**When to use:**
- Comparing 2-4 methods, options, or items
- Each item has 3-5 attributes
- Content is descriptive (not just numbers)

**Example - Instead of this table:**
\`\`\`
❌ Table (breaks on mobile):
| Method | Speed | Cost | Ease | When to Use |
|--------|-------|------|------|-------------|
| A | Fast | High | Easy | Small projects |
| B | Slow | Low | Hard | Large projects |
\`\`\`

**Use this card format:**
\`\`\`
✅ Cards (mobile-friendly):

### Method A
- **Speed:** Fast
- **Cost:** High  
- **Ease of Use:** Easy
- **Best For:** Small projects with tight deadlines

### Method B
- **Speed:** Slower
- **Cost:** Low
- **Ease of Use:** More complex
- **Best For:** Large projects where cost is primary concern

**Bottom Line:** Choose A for speed, B for cost savings.
\`\`\`

**Benefits:**
- Perfect readability on any screen size
- No horizontal scrolling
- Easy to scan and compare
- Better for APK WebView

---

### ✅ FORMAT 2: DEFINITION LIST (Best for key-value pairs)

**When to use:**
- Simple key-value relationships
- Definitions or specifications
- Short facts or data points

**Example - Instead of this table:**
\`\`\`
❌ Table (unnecessary):
| Aspect | Value |
|--------|-------|
| Rate | 12% |
| Period | 5 years |
| Base | Gross income |
\`\`\`

**Use this list format:**
\`\`\`
✅ Definition list (cleaner):

**VAT Rate:** 12%  
**Withholding Period:** 5 years  
**Tax Base:** Gross income  
**Effective Date:** January 1, 2024
\`\`\`

**Benefits:**
- Cleaner appearance
- No table overhead
- Easier to read on mobile
- Faster to scan

---

### ✅ FORMAT 3: STACKED SECTIONS (Best for detailed comparisons)

**When to use:**
- Detailed comparison of items
- Each item needs paragraph-length content
- More than 5 attributes per item

**Example - Instead of wide table:**
\`\`\`
❌ Wide table (terrible on mobile):
| Method | Pros | Cons | Formula | Example | Use Case |
\`\`\`

**Use stacked sections:**
\`\`\`
✅ Stacked sections:

## Straight-Line Depreciation

**Formula**
(Cost - Salvage) ÷ Useful Life

**Advantages**
- Simple to calculate
- Easy to understand
- Consistent expense each period

**Disadvantages**
- Doesn't reflect actual usage pattern
- May not match revenue generation

**Best For**
Assets with consistent usage patterns like buildings, furniture

**Example**
Equipment: ₱100,000 | Salvage: ₱10,000 | Life: 5 years
Annual depreciation = ₱18,000

---

## Declining Balance Depreciation

**Formula**
(Cost - Accumulated Depreciation) × Rate

**Advantages**
- Higher expense early (matches revenue)
- Tax benefits in early years
- Reflects faster obsolescence

**Disadvantages**
- More complex calculation
- Requires tracking accumulated depreciation

**Best For**
Technology, vehicles, equipment with high early-year usage

**Example**
Same equipment at 40% rate
Year 1: ₱40,000 | Year 2: ₱24,000 | Year 3: ₱14,400
\`\`\`

**Benefits:**
- Unlimited detail possible
- Perfect on any screen
- Natural reading flow
- Can include examples inline

---

### ✅ FORMAT 4: SIMPLE 2-COLUMN TABLE (Only when necessary)

**When to use:**
- ONLY when data is truly tabular
- Simple number comparisons
- Very short cells (<20 chars)
- No more than 6-8 rows

**Example - Acceptable table:**
\`\`\`
✅ Simple 2-column (mobile-safe):

| Tax Rate | Income Range |
|----------|--------------|
| 0% | Up to ₱250,000 |
| 15% | ₱250,001 - ₱400,000 |
| 20% | ₱400,001 - ₱800,000 |
| 25% | ₱800,001 - ₱2,000,000 |
| 30% | ₱2,000,001 - ₱8,000,000 |
| 35% | Over ₱8,000,000 |
\`\`\`

**Requirements:**
- Maximum 2 columns
- Cells under 20 characters
- Clear, scannable data
- Under 10 rows if possible

---

### ✅ FORMAT 5: NESTED BULLETS (Best for hierarchical data)

**When to use:**
- Categorized information
- Hierarchical relationships
- Multiple related items

**Example - Instead of table:**
\`\`\`
❌ Table with categories:
| Category | Item | Details |
|----------|------|---------|
| Current | Cash | ... |
| Current | AR | ... |
| Non-current | PPE | ... |
\`\`\`

**Use nested bullets:**
\`\`\`
✅ Nested bullets:

**Current Assets**
- **Cash and Cash Equivalents**
  - Bank accounts
  - Petty cash
  - 3-month or less investments
  
- **Accounts Receivable**
  - Trade receivables
  - Less: Allowance for doubtful accounts
  - Net realizable value

**Non-Current Assets**
- **Property, Plant & Equipment**
  - Land (not depreciated)
  - Buildings (depreciated)
  - Equipment (depreciated)
  - Less: Accumulated depreciation
\`\`\`

**Benefits:**
- Shows relationships clearly
- Natural hierarchy
- Easy to expand or collapse mentally
- Perfect for mobile

---

### ✅ FORMAT 6: SIDE-BY-SIDE COMPARISON (Best for 2 items)

**When to use:**
- Exactly 2 items to compare
- Multiple comparison points
- Want visual symmetry

**Example:**
\`\`\`
✅ Side-by-side:

### RCIT (Regular Corporate Income Tax)
**Tax Base:** Taxable Income  
**Rate:** 25% (Regular) or 20% (MSME)  
**When to Pay:** Always computed  
**Qualification:** MSME must meet TI ≤ ₱5M AND Assets ≤ ₱100M

### MCIT (Minimum Corporate Income Tax)
**Tax Base:** Gross Income  
**Rate:** 2%  
**When to Pay:** Starting 4th taxable year only  
**Qualification:** None (applies to all)

**Decision Rule:** Compare both, pay whichever is HIGHER.
\`\`\`

**Benefits:**
- Natural comparison structure
- Easy to read side-by-side on desktop
- Stacks nicely on mobile
- Clear decision point

---

## 🎯 DECISION TREE: When to Use What Format

\`\`\`
Need to present structured data?
│
├─ 2 items to compare?
│  └─ Use SIDE-BY-SIDE COMPARISON or COMPARISON CARDS
│
├─ Key-value pairs (name: value)?
│  └─ Use DEFINITION LIST
│
├─ Detailed comparison (3+ items)?
│  └─ Use COMPARISON CARDS or STACKED SECTIONS
│
├─ Hierarchical/categorized data?
│  └─ Use NESTED BULLETS
│
├─ Simple numbers only (<20 chars, 2 columns)?
│  └─ Use SIMPLE 2-COLUMN TABLE
│
└─ Complex data with long content?
   └─ Use STACKED SECTIONS
\`\`\`

---

## 📱 MOBILE/APK-SPECIFIC GUIDELINES

### **Screen Size Assumptions:**
- Mobile: 375px - 414px width (iPhone SE to iPhone Pro Max)
- APK: Similar to mobile, WebView constraints
- Tablet: 768px+ width (rare, still prefer mobile-first)

### **Content Width Limits:**
- Each line should fit in ~35-40 characters on mobile
- Tables with cells >20 chars will wrap badly
- Prefer vertical stacking over horizontal layout

### **APK WebView Considerations:**
- Tables render slower in WebView
- Horizontal scroll is clunky in APK
- Cards and lists render perfectly
- Native-like experience is better

### **Touch Target Size:**
- List items easier to tap than table cells
- Better spacing in cards vs tables
- More forgiving for thumb navigation

---

## ⚠️ WHEN TABLES BREAK ON MOBILE

**Table with 3 columns:**
\`\`\`
On desktop: ✅ Looks fine
On mobile:  ❌ Squished, hard to read, needs horizontal scroll
\`\`\`

**Table with 4+ columns:**
\`\`\`
On desktop: ⚠️ Cramped but readable
On mobile:  ❌ Completely broken, unusable
\`\`\`

**Table with long cell content:**
\`\`\`
On desktop: ⚠️ Cells wrap awkwardly
On mobile:  ❌ Total mess, unreadable
\`\`\`

**Alternative format (cards/lists):**
\`\`\`
On desktop: ✅ Clean and readable
On mobile:  ✅ Perfect, natural flow
\`\`\`

---

## 📊 USAGE STATISTICS

**From user analytics:**
- 40% of users on mobile/APK
- 35% on desktop
- 25% on tablet

**Table rendering issues:**
- 3-column tables: 60% report issues on mobile
- 4+ column tables: 95% report issues on mobile
- 2-column tables: 10% report issues (acceptable)

**Alternative format satisfaction:**
- Comparison cards: 95% positive feedback
- Definition lists: 92% positive feedback
- Stacked sections: 89% positive feedback

**Recommendation:** Default to alternative formats, use tables only when truly necessary and ≤2 columns.

---

## ✅ SUMMARY: MOBILE-FIRST TABLE ALTERNATIVES

**Default mindset:** Don't use tables unless absolutely necessary

**Best alternatives:**
1. **Comparison Cards** - Best for 2-4 items with multiple attributes
2. **Definition Lists** - Best for key-value pairs
3. **Stacked Sections** - Best for detailed comparisons
4. **Nested Bullets** - Best for hierarchical data
5. **Side-by-Side** - Best for exactly 2 items
6. **Simple 2-Column** - Last resort, only if data truly tabular

**Mobile testing:**
- Always imagine viewing on iPhone SE (smallest common screen)
- If it doesn't look good at 375px width, redesign
- Vertical stacking beats horizontal cramming

**APK considerations:**
- WebView has limitations
- Simpler is always better
- Native-like formatting preferred
- Cards and lists feel more app-like

**Quality rule:**
> If you think "Will this table look okay on mobile?" → The answer is NO. Use an alternative format.
`;

/**
 * Check if content should use table or alternative format
 */
export function shouldUseTable(
  itemCount: number,
  attributeCount: number,
  avgCellLength: number
): { useTable: boolean; reason: string; suggestedFormat: string } {
  
  // Too many items - use sections
  if (itemCount > 4) {
    return {
      useTable: false,
      reason: 'Too many items (>4) - hard to scan on mobile',
      suggestedFormat: 'Use stacked sections or nested bullets'
    };
  }
  
  // Too many attributes - use cards
  if (attributeCount > 3) {
    return {
      useTable: false,
      reason: 'Too many attributes (>3) - table too wide for mobile',
      suggestedFormat: 'Use comparison cards'
    };
  }
  
  // Long content - use cards or sections
  if (avgCellLength > 25) {
    return {
      useTable: false,
      reason: 'Cell content too long (>25 chars) - wraps badly on mobile',
      suggestedFormat: 'Use comparison cards or stacked sections'
    };
  }
  
  // 2-3 items comparison - prefer cards
  if (itemCount <= 3 && attributeCount >= 3) {
    return {
      useTable: false,
      reason: '2-3 items with multiple attributes - cards are better',
      suggestedFormat: 'Use comparison cards (more readable)'
    };
  }
  
  // Key-value pairs - use definition list
  if (attributeCount === 2 && itemCount <= 6 && avgCellLength < 20) {
    return {
      useTable: false,
      reason: 'Key-value pairs - definition list is cleaner',
      suggestedFormat: 'Use definition list format'
    };
  }
  
  // Only allow table if ALL conditions met
  if (itemCount <= 8 && attributeCount === 2 && avgCellLength < 20) {
    return {
      useTable: true,
      reason: 'Simple 2-column table with short cells - acceptable',
      suggestedFormat: 'Simple 2-column table (keep cells short)'
    };
  }
  
  // Default: don't use table
  return {
    useTable: false,
    reason: 'Table not ideal for this data - alternative format better',
    suggestedFormat: 'Choose appropriate format from decision tree'
  };
}

/**
 * Generate comparison card format
 */
export function generateComparisonCards(
  items: Array<{ name: string; attributes: Record<string, string> }>
): string {
  let output = '';
  
  items.forEach((item, index) => {
    output += `### ${item.name}\n\n`;
    
    Object.entries(item.attributes).forEach(([key, value]) => {
      output += `- **${key}:** ${value}\n`;
    });
    
    if (index < items.length - 1) {
      output += '\n---\n\n';
    }
  });
  
  return output;
}

/**
 * Generate definition list format
 */
export function generateDefinitionList(
  items: Array<{ key: string; value: string }>
): string {
  return items.map(item => `**${item.key}:** ${item.value}`).join('  \n');
}

/**
 * Estimate mobile readability score
 */
export function estimateMobileReadability(markdown: string): {
  score: number;
  issues: string[];
} {
  const issues: string[] = [];
  let score = 100;
  
  // Check for tables
  const tableMatches = markdown.match(/\|[^\n]+\|/g);
  if (tableMatches && tableMatches.length > 0) {
    const tableRows = tableMatches;
    const pipeCounts = tableRows.map(row => (row.match(/\|/g) || []).length);
    const maxCols = Math.max(...pipeCounts) - 1;
    
    if (maxCols > 2) {
      issues.push(`Table has ${maxCols} columns (>2 is bad for mobile)`);
      score -= 30;
    } else if (maxCols === 2) {
      issues.push('Table has 2 columns (acceptable but cards might be better)');
      score -= 5;
    }
    
    // Check cell length
    const cells = markdown.match(/\|([^\|\n]+)\|/g);
    if (cells) {
      const longCells = cells.filter(cell => cell.replace(/\|/g, '').trim().length > 25);
      if (longCells.length > 0) {
        issues.push(`${longCells.length} cells >25 chars (too long for mobile)`);
        score -= 20;
      }
    }
  }
  
  // Check line length
  const lines = markdown.split('\n');
  const longLines = lines.filter(line => line.length > 80);
  if (longLines.length > lines.length * 0.3) {
    issues.push('Many long lines (>80 chars) - may need horizontal scroll');
    score -= 10;
  }
  
  return { score: Math.max(0, score), issues };
}
